import React, { useEffect, useState } from 'react';
import { FiSmartphone, FiMonitor, FiTablet, FiLoader, FiRefreshCw, FiX, FiCamera } from 'react-icons/fi';
import Modal from 'components/basicos/Modal';
import BridgeCapture from './BridgeCapture';
import { useDeviceBridge, tieneCamara, subirFoto } from 'shared/deviceBridge';

const ICONOS = { movil: FiSmartphone, tablet: FiTablet, pc: FiMonitor };

const S = {
  lista: { display: 'flex', flexDirection: 'column', gap: '10px' },
  dispositivo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid var(--agx-border)',
    background: 'var(--agx-bg)',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'left',
    width: '100%',
    fontFamily: 'inherit',
  },
  local: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '13px 14px',
    borderRadius: '12px',
    border: '1.5px solid var(--agx-pri2)',
    background: 'rgba(41,82,204,0.06)',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'left',
    width: '100%',
    fontFamily: 'inherit',
    marginBottom: '6px',
  },
  nombre: { fontWeight: 600, fontSize: '14px', color: 'var(--agx-text)' },
  sub: { fontSize: '12px', color: 'var(--agx-muted)' },
  estado: (online) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: online ? '#16a34a' : '#9ca3af',
    flexShrink: 0,
  }),
  vacio: { textAlign: 'center', color: 'var(--agx-muted)', padding: '24px 0', fontSize: '14px' },
  progreso: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '28px 0' },
  error: {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '12px',
    fontSize: '13px',
    marginBottom: '12px',
  },
  acciones: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '9px 16px',
    borderRadius: '10px',
    border: 'none',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default function BridgeRequestModal({ open, onClose, tipo, titulo, params = {}, onResultado }) {
  const { dispositivos, listarDispositivos, solicitar } = useDeviceBridge();
  const [enviandoA, setEnviandoA] = useState(null);
  const [error, setError] = useState(null);
  const [camaraLocal, setCamaraLocal] = useState(false);
  const [usandoLocal, setUsandoLocal] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  useEffect(() => {
    if (open) {
      setEnviandoA(null);
      setError(null);
      setUsandoLocal(false);
      setSubiendo(false);
      listarDispositivos();
      tieneCamara().then(setCamaraLocal).catch(() => setCamaraLocal(false));
    }
  }, [open, listarDispositivos]);

  const enviar = async (device) => {
    setError(null);
    setEnviandoA(device);
    try {
      const res = await solicitar(device.device_id, tipo, params);
      if (onResultado) onResultado(res);
      onClose();
    } catch (e) {
      const msg = e?.message || 'No se pudo contactar el dispositivo';
      setError(msg === 'peticion_expirada' ? 'El dispositivo no respondió a tiempo.' : msg);
      setEnviandoA(null);
    }
  };

  const handleLocalCompletado = async (resultado) => {
    setError(null);
    try {
      if (tipo === 'escanear_codigo' && resultado?.codigo) {
        if (onResultado) onResultado({ estado: 'completado', resultado: { codigo: resultado.codigo } });
        onClose();
        return;
      }
      if (tipo === 'foto' && resultado?.foto) {
        setSubiendo(true);
        const payload = await subirFoto(resultado.foto, params);
        if (onResultado) onResultado({ estado: 'completado', resultado: payload });
        onClose();
        return;
      }
    } catch (e) {
      setError(e?.message || 'No se pudo completar');
      setSubiendo(false);
    }
  };

  const etiquetaLocal = tipo === 'escanear_codigo' ? 'Escanear en este dispositivo' : 'Tomar foto en este dispositivo';

  return (
    <Modal open={open} onClose={onClose} title={titulo || 'Elegir dispositivo'} width="sm">
      {error && <div style={S.error}>{error}</div>}

      {usandoLocal ? (
        <BridgeCapture
          tipo={tipo}
          onCompletado={handleLocalCompletado}
          onCancelar={() => setUsandoLocal(false)}
        />
      ) : enviandoA ? (
        <div style={S.progreso}>
          <FiLoader size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--agx-muted)' }} />
          <div style={S.nombre}>Esperando respuesta en {enviandoA.nombre}…</div>
          <div style={S.sub}>Abre la notificación en ese dispositivo.</div>
        </div>
      ) : (
        <>
          {camaraLocal && !subiendo && (
            <button style={S.local} onClick={() => setUsandoLocal(true)}>
              <FiCamera size={20} style={{ color: 'var(--agx-pri2)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={S.nombre}>{etiquetaLocal}</div>
                <div style={S.sub}>Usa la cámara de este dispositivo directamente.</div>
              </div>
            </button>
          )}

          <div style={S.lista}>
            {dispositivos.length === 0 ? (
              <div style={S.vacio}>
                No hay otros dispositivos con tu sesión iniciada.
                <br />
                Inicia sesión en el celular con el mismo usuario.
              </div>
            ) : (
              dispositivos.map((d) => {
                const Icono = ICONOS[d.tipo] || FiSmartphone;
                return (
                  <button key={d.device_id} style={S.dispositivo} onClick={() => enviar(d)}>
                    <Icono size={20} style={{ color: 'var(--agx-text)', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={S.nombre}>{d.nombre}</div>
                      <div style={S.sub}>{d.online ? 'Conectado' : 'Fuera de línea · se enviará notificación'}</div>
                    </div>
                    <span style={S.estado(d.online)} />
                  </button>
                );
              })
            )}
          </div>

          <div style={S.acciones}>
            <button
              style={{ ...S.btn, background: 'transparent', color: 'var(--agx-muted)' }}
              onClick={() => listarDispositivos()}
            >
              <FiRefreshCw size={14} /> Actualizar
            </button>
            <button
              style={{ ...S.btn, background: '#f1f5f9', color: '#475569' }}
              onClick={onClose}
            >
              <FiX size={14} /> Cancelar
            </button>
          </div>
        </>
      )}
      {subiendo && (
        <div style={{ textAlign: 'center', color: 'var(--agx-muted)', paddingTop: 12, fontSize: 13 }}>
          Subiendo…
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Modal>
  );
}
